import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import {
  useForm,
  type SubmitHandler,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ChevronRight, Image as ImageIcon, Info, Ruler, Settings2 } from 'lucide-react';
import {
  Condition,
  InventoryStatus,
  INVENTORY_ID_PATTERN,
  InventoryLocation,
  MileageUnit,
  isFinanced,
  financingRuleAllows,
  type Inventory,
  type InventoryImageInput,
  type Lookup,
  type LookupInput,
} from '@ncc/shared';
import { ApiError, api } from '../lib/api';
import { ImageManager } from './ImageManager';

// Empty <select>/number inputs submit '' — turn that into null BEFORE coercion.
// Without this, z.coerce.number()('') === 0, which fails the API's
// `.positive().nullable()` id rules (→ 400) and stores $0 for blank prices.
const emptyToNull = (v: unknown) => (v === '' || v == null ? null : v);
const nullableId = z.preprocess(emptyToNull, z.coerce.number().int().positive().nullable());
const nullableInt = z.preprocess(emptyToNull, z.coerce.number().int().nonnegative().nullable());
const nullableMoney = z.preprocess(emptyToNull, z.coerce.number().nonnegative().nullable());
const nullableMileageUnit = z.preprocess(emptyToNull, MileageUnit.nullable());
const nullableLocation = z.preprocess(emptyToNull, InventoryLocation.nullable());

// Optional free text with the SAME cap the API enforces. These must mirror
// InventoryBase in @ncc/shared: when this schema was laxer than the server's,
// an overlong value sailed past the form and came back as a raw "API 400 —
// enclosed_door_size: String must contain at most 40 character(s)" toast,
// naming a field that is collapsed out of sight and that the user never
// touched. Too long is now an inline error on the field itself.
const text = (max: number) => z.string().max(max, `Keep this under ${max} characters`).optional();

const FormSchema = z.object({
  inventory_id: z.string().regex(INVENTORY_ID_PATTERN, '6 digits, dash, 2 digits (e.g. 123456-24)'),
  status: InventoryStatus,
  year: z.coerce.number().int().min(1900).max(2100),
  regular_price: nullableMoney,
  sale_price: nullableMoney,
  biweekly_payment: nullableMoney,
  financing_disabled: z.boolean().optional(),
  vin: text(17),
  brand_id: nullableId,
  type_id: nullableId,
  tag_id: nullableId,
  vehicle_name: z.string().min(1).max(200, 'Keep this under 200 characters'),
  model_number: z.string().min(1).max(100, 'Keep this under 100 characters'),
  mileage: nullableInt,
  mileage_unit: nullableMileageUnit,
  location: nullableLocation,
  color: text(60),
  condition: Condition,
  youtube_link: z.string().url().or(z.literal('')).optional(),
  description: z.string().min(1).max(20_000, 'Keep this under 20,000 characters'),

  gawr: nullableInt,
  gross_capacity: nullableInt,
  curb_weight: nullableInt,
  payload_capacity: nullableInt,

  tie_downs: text(200),
  lights: text(200),
  hitch: text(200),
  jack: text(200),
  axles: text(200),
  suspensions: text(200),
  tires_wheels: text(200),
  rim_size: text(200),
  trailer_plug: text(200),
  ramp: text(200),
  frame_construction: text(200),
  deck_construction: text(200),

  overall_length: text(200),
  overall_width: text(200),
  overall_height: text(200),
  interior_length: text(200),
  interior_width: text(200),
  interior_height: text(200),
  deck_length: text(200),
  deck_width: text(200),
  deck_height: text(200),
  enclosed_door_size: text(200),
});
export type InventoryFormValues = z.infer<typeof FormSchema>;

type Props = {
  initial?: Inventory;
  submitLabel: string;
  // `changed` holds only the fields whose value differs from the record the form
  // opened with — the right payload for a PATCH. Creates ignore it and send
  // `values` in full. Empty object => the user changed no field.
  onSubmit: (
    values: InventoryFormValues,
    images: InventoryImageInput[],
    changed: Partial<InventoryFormValues>,
  ) => Promise<void>;
  initialImages?: InventoryImageInput[];
};

export function InventoryForm({ initial, submitLabel, onSubmit, initialImages }: Props) {
  const isNew = !initial;

  const form = useForm<InventoryFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: useMemo(() => toDefaults(initial), [initial]),
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted },
    watch,
    setValue,
  } = form;

  // A blocked submit is otherwise invisible — react-hook-form simply declines to
  // call onSubmit, so nothing happens and no request is sent. Name the offending
  // fields next to the button; the per-field message alone is easy to miss, and
  // for the spec/dimension fields it sits inside a collapsed disclosure.
  const blockedBy = (Object.keys(errors) as (keyof InventoryFormValues)[]).map(fieldLabel);

  const imagesRef = useRef<InventoryImageInput[]>(
    initialImages ??
      (initial?.images.map((i) => ({ url: i.url, image_id: i.image_id, position: i.position })) ??
        []),
  );

  // Brand / type / tag option lists are fetched inside LookupSelect, which owns
  // creating new entries too.

  const currentYear = watch('year');
  const currentInvId = watch('inventory_id');

  // One financing rule, shared with the widgets (isFinanced in @ncc/shared): no
  // payments on a used unit of ANY type — used trailers and used vehicles alike
  // are priced case-by-case — and none on a classic vehicle whatever its
  // condition. The site renders nothing for those, so don't offer the override
  // field either; an amount typed there would never reach a buyer.
  //
  // The type list is already in the query cache from the Inventory type picker
  // below (same key), so this reads it instead of fetching again. Until it lands
  // the type reads as unknown and Condition alone decides, which is the answer
  // for every trailer anyway.
  const inventoryTypes = useQuery({
    queryKey: ['inventory-types'],
    queryFn: api.listInventoryTypes,
  });
  const currentTypeId = watch('type_id');
  const financingDisabled = watch('financing_disabled') ?? false;
  const typeName = inventoryTypes.data?.find((t) => t.id === currentTypeId)?.name ?? null;

  // Two different questions, and the form needs both answers.
  //
  // ruleAllows — may this listing be financed at all? Decides whether the
  // off-switch is shown: on a used unit or a classic vehicle there is nothing to
  // turn off, and a checkbox there would imply it could be turned back ON, which
  // it cannot. The type/condition rule is the floor.
  //
  // showsFinancing — is it financed as things currently stand? Decides the
  // bi-weekly override field, which has nothing to override once financing is
  // switched off on this unit.
  const ruleAllows = financingRuleAllows({ condition: watch('condition'), type_name: typeName });
  const showsFinancing = isFinanced({
    condition: watch('condition'),
    type_name: typeName,
    financing_disabled: financingDisabled,
  });

  // Suggest the "-YY" suffix on the Inventory ID placeholder using the year.
  // We don't auto-fill the field — it requires the VIN portion from the user.
  const invIdPlaceholder = useMemo(() => {
    const yy = currentYear != null ? String(currentYear).slice(-2).padStart(2, '0') : 'YY';
    return `123456-${yy}`;
  }, [currentYear]);

  // Debounced uniqueness check: when the user finishes typing a valid
  // inventory_id on a new listing, look it up to warn early.
  const [invIdStatus, setInvIdStatus] = useState<
    null | { kind: 'checking' } | { kind: 'taken'; existingId: number } | { kind: 'ok' }
  >(null);

  useEffect(() => {
    if (!isNew || !currentInvId) {
      setInvIdStatus(null);
      return;
    }
    if (!INVENTORY_ID_PATTERN.test(currentInvId)) {
      setInvIdStatus(null);
      return;
    }
    let cancelled = false;
    setInvIdStatus({ kind: 'checking' });
    const t = setTimeout(async () => {
      try {
        const r = await api.listInventory({ inventory_id: currentInvId, page_size: 1 });
        if (cancelled) return;
        if (r.items.length > 0) {
          setInvIdStatus({ kind: 'taken', existingId: r.items[0]!.id });
        } else {
          setInvIdStatus({ kind: 'ok' });
        }
      } catch {
        if (!cancelled) setInvIdStatus(null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [currentInvId, isNew]);

  // Same treatment for the VIN. It has a unique index of its own (migration
  // 0004) but had no check here, so the first sign of a clash was the save
  // failing — and staff hit it precisely because a VIN gets copied off a
  // similar unit. 54 of the ~190 listings carry one, so the collision space is
  // real.
  //
  // Two differences from the inventory_id check above. It runs when EDITING as
  // well as creating, because pasting a duplicate VIN into an existing listing
  // is rejected just the same; and it therefore has to ignore a match against
  // the listing being edited, which is not a clash. It also matches on any
  // non-empty value rather than waiting for a valid pattern: the unique index
  // applies to whatever is stored, so a short entry can clash too.
  const currentVin = watch('vin');
  const [vinStatus, setVinStatus] = useState<
    null | { kind: 'checking' } | { kind: 'taken'; existingId: number } | { kind: 'ok' }
  >(null);

  useEffect(() => {
    const vin = (currentVin ?? '').trim();
    if (!vin) {
      setVinStatus(null);
      return;
    }
    let cancelled = false;
    setVinStatus({ kind: 'checking' });
    const t = setTimeout(async () => {
      try {
        const r = await api.listInventory({ vin, page_size: 1 });
        if (cancelled) return;
        // Matching ourselves is not a conflict — only another listing is.
        const clash = r.items.find((i) => i.id !== initial?.id);
        setVinStatus(clash ? { kind: 'taken', existingId: clash.id } : { kind: 'ok' });
      } catch {
        if (!cancelled) setVinStatus(null);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [currentVin, initial?.id]);

  // There was an autosave here that POSTed a draft every 8 seconds while the
  // form was dirty. It never stopped and never checked whether anything had
  // changed since the last save, so a New Inventory tab left open hammered the
  // API indefinitely — 38 requests in one 4.5-minute sample. Removed; the form
  // saves when you submit it.

  const submit: SubmitHandler<InventoryFormValues> = async (values) => {
    // Clear the override on an unfinanced listing rather than keeping whatever
    // was typed before it became one: the input is hidden and the site ignores
    // the value, so leaving it stored would only mislead whoever reads the row
    // or the Sheet.
    const cleaned = {
      ...values,
      // The override is hidden and the site ignores it once the listing isn't
      // financed, so don't keep whatever was typed before it became one — a
      // stored value would only mislead whoever reads the row or the Sheet.
      ...(showsFinancing ? null : { biweekly_payment: null }),
      // Same for the off-switch on a listing the rule already excludes: it can't
      // do anything there, and it reads as though someone made a call about a
      // used unit's financing that was never theirs to make.
      ...(ruleAllows ? null : { financing_disabled: false }),
    };
    await onSubmit(cleaned, imagesRef.current, changedFields(cleaned, initial));
  };

  const errorOf = (key: keyof InventoryFormValues) =>
    errors[key] ? <div className="error-text">{errors[key]?.message as string}</div> : null;

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      {/* Photos first — most listings live or die by their photos. */}
      <h3 className="section-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ImageIcon size={14} /> Photos
        </span>
      </h3>
      <ImageManager
        initial={imagesRef.current}
        onChange={(next) => {
          imagesRef.current = next;
        }}
      />

      <h3 className="section-title">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Info size={14} /> Required information
        </span>
      </h3>
      <div className="grid-form">
        <div className="field-span-4">
          <label>Inventory ID *</label>
          <input placeholder={invIdPlaceholder} {...register('inventory_id')} />
          {errorOf('inventory_id')}
          {!errors.inventory_id && isNew && invIdStatus?.kind === 'checking' ? (
            <div className="hint-text">Checking availability…</div>
          ) : !errors.inventory_id && isNew && invIdStatus?.kind === 'ok' ? (
            <div className="ok-text">Available</div>
          ) : !errors.inventory_id && isNew && invIdStatus?.kind === 'taken' ? (
            <div className="error-text">
              In use by another listing.{' '}
              <a href={`/edit/${invIdStatus.existingId}`}>Edit it instead</a>
            </div>
          ) : (
            <div className="hint-text">Last 6 digits of VIN, dash, last 2 digits of year.</div>
          )}
        </div>
        <div className="field-span-4">
          <label>VIN</label>
          <input placeholder="Full VIN (private)" maxLength={17} {...register('vin')} />
          {errorOf('vin')}
          {!errors.vin && vinStatus?.kind === 'checking' ? (
            <div className="hint-text">Checking availability…</div>
          ) : !errors.vin && vinStatus?.kind === 'ok' ? (
            <div className="ok-text">Available</div>
          ) : !errors.vin && vinStatus?.kind === 'taken' ? (
            <div className="error-text">
              Already on another listing.{' '}
              <a href={`/edit/${vinStatus.existingId}`}>Edit it instead</a>
            </div>
          ) : (
            <div className="hint-text">Stored for records — never shown on the public site.</div>
          )}
        </div>
        <div className="field-span-4">
          <label>Year *</label>
          <input type="number" {...register('year')} />
          {errorOf('year')}
        </div>
        <div className="field-span-4">
          <label>Condition *</label>
          <select {...register('condition')}>
            <option value="New">New</option>
            <option value="Used">Used</option>
          </select>
        </div>

        <div className="field-span-6">
          <label>Vehicle name *</label>
          <input placeholder="e.g. Tilt Slide Axle Trailer" {...register('vehicle_name')} />
          {errorOf('vehicle_name')}
        </div>
        <div className="field-span-3">
          <LookupSelect
            name="brand_id"
            label="Brand *"
            noun="brand"
            queryKey="brands"
            list={api.listBrands}
            create={api.createBrand}
            newPlaceholder="e.g. Midsota"
            register={register}
            setValue={setValue}
            error={errorOf('brand_id')}
          />
        </div>
        <div className="field-span-3">
          <LookupSelect
            name="type_id"
            label="Inventory type *"
            noun="inventory type"
            queryKey="inventory-types"
            list={api.listInventoryTypes}
            create={api.createInventoryType}
            newPlaceholder="e.g. Dump Trailers"
            register={register}
            setValue={setValue}
            error={errorOf('type_id')}
          />
        </div>

        <div className="field-span-4">
          <label>Model number *</label>
          <input {...register('model_number')} />
          {errorOf('model_number')}
        </div>
        <div className="field-span-4">
          <label>Regular price</label>
          <input type="number" step="0.01" placeholder="$" {...register('regular_price')} />
        </div>
        <div className="field-span-4">
          <label>Sale price</label>
          <input type="number" step="0.01" placeholder="$" {...register('sale_price')} />
        </div>

        <div className="field-span-4">
          <label>Status</label>
          <select {...register('status')}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="hidden">Hidden</option>
            <option value="sold">Sold</option>
          </select>
        </div>
        <div className="field-span-4">
          <LookupSelect
            name="tag_id"
            label="Tag"
            noun="tag"
            queryKey="tags"
            list={api.listTags}
            create={api.createTag}
            newPlaceholder="e.g. Summer Sale"
            register={register}
            setValue={setValue}
          />
        </div>
        <div className="field-span-4">
          <label>Location</label>
          <select {...register('location')}>
            <option value="">— not shown</option>
            {InventoryLocation.options.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
          <div className="hint-text">Which lot the unit is on. Shown in the price box on the site.</div>
        </div>
        {ruleAllows && (
          <div className="field-span-4">
            <label>Financing</label>
            <label className="checkbox-inline">
              <input type="checkbox" {...register('financing_disabled')} />
              <span>Don’t show payment estimates on this listing</span>
            </label>
            <div className="hint-text">
              {financingDisabled
                ? 'Hidden — this listing shows its price only, with no bi-weekly or lease figure.'
                : 'Showing bi-weekly and lease estimates, calculated from the sale price.'}
            </div>
          </div>
        )}
        {showsFinancing && (
          <div className="field-span-4">
            <label>Bi-weekly payment override</label>
            <input
              type="number"
              step="0.01"
              placeholder="auto-calculated"
              {...register('biweekly_payment')}
            />
            <div className="hint-text">Leave blank to auto-calculate from the sale price.</div>
          </div>
        )}

        <div className="field-span-12">
          <label>Description *</label>
          <textarea
            rows={6}
            placeholder="Sales-ready description that will appear on the public listing."
            {...register('description')}
          />
          {errorOf('description')}
        </div>
      </div>

      {/* Specs and dimensions hidden by default — most listings don't need
          most of these fields. Click to expand. */}
      <details className="disclosure">
        <summary>
          <ChevronRight size={16} className="chev" />
          <Settings2 size={16} />
          Specifications &amp; equipment
          <span style={{ color: 'var(--muted)', fontSize: '0.82rem', marginLeft: 'auto' }}>
            optional
          </span>
        </summary>
        <div className="disclosure-body">
          <div className="grid-form">
            <div className="field-span-3">
              <label>Mileage</label>
              {/* Older classics read in miles, newer units in km. Blank unit
                  prints the bare number on the site, as older listings do. */}
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="number" style={{ flex: 1, minWidth: 0 }} {...register('mileage')} />
                <select
                  style={{ width: 'auto' }}
                  title="Shown after the mileage on the site, e.g. “94,187 mi”"
                  aria-label="Mileage unit"
                  {...register('mileage_unit')}
                >
                  <option value="">—</option>
                  <option value="km">km</option>
                  <option value="mi">miles</option>
                </select>
              </div>
            </div>
            <div className="field-span-3">
              <label>Color</label>
              <input {...register('color')} />
            </div>
            <div className="field-span-3">
              <label>GAWR (lbs/axle)</label>
              <input type="number" {...register('gawr')} />
            </div>
            <div className="field-span-3">
              <label>GVWR (lbs)</label>
              <input type="number" {...register('gross_capacity')} />
            </div>
            <div className="field-span-3">
              <label>Dry weight (lbs)</label>
              <input type="number" {...register('curb_weight')} />
            </div>
            <div className="field-span-3">
              <label>Payload capacity (lbs)</label>
              <input type="number" {...register('payload_capacity')} />
            </div>
            <div className="field-span-6">
              <label>YouTube link</label>
              <input placeholder="https://youtube.com/watch?v=…" {...register('youtube_link')} />
              {errorOf('youtube_link')}
            </div>
            {(
              [
                ['tie_downs', 'Tie downs'],
                ['lights', 'Lights'],
                ['hitch', 'Hitch'],
                ['jack', 'Jack'],
                ['axles', 'Axles'],
                ['suspensions', 'Suspensions'],
                ['tires_wheels', 'Tires / wheels'],
                ['rim_size', 'Rim size'],
                ['trailer_plug', 'Trailer plug'],
                ['ramp', 'Ramp'],
                ['frame_construction', 'Frame construction'],
                ['deck_construction', 'Deck construction'],
              ] as const
            ).map(([name, label]) => (
              <div key={name} className="field-span-4">
                <label>{label}</label>
                <input {...register(name)} />
              </div>
            ))}
          </div>
        </div>
      </details>

      <details className="disclosure">
        <summary>
          <ChevronRight size={16} className="chev" />
          <Ruler size={16} />
          Dimensions
          <span style={{ color: 'var(--muted)', fontSize: '0.82rem', marginLeft: 'auto' }}>
            optional
          </span>
        </summary>
        <div className="disclosure-body">
          <div className="grid-form">
            {(
              [
                ['overall_length', 'Overall length'],
                ['overall_width', 'Overall width'],
                ['overall_height', 'Overall height'],
                ['interior_length', 'Interior length'],
                ['interior_width', 'Interior width'],
                ['interior_height', 'Interior height'],
                ['deck_length', 'Deck length'],
                ['deck_width', 'Deck width'],
                ['deck_height', 'Deck height'],
                ['enclosed_door_size', 'Enclosed door size'],
              ] as const
            ).map(([name, label]) => (
              <div key={name} className="field-span-3">
                <label>{label}</label>
                <input {...register(name)} />
              </div>
            ))}
          </div>
        </div>
      </details>

      {isSubmitted && blockedBy.length > 0 ? (
        <div className="error-text" role="alert">
          Not saved — fix {blockedBy.join(', ')} above, then save again.
        </div>
      ) : null}
      <div className="toolbar form-actions">
        <button
          className="primary"
          type="submit"
          disabled={isSubmitting || invIdStatus?.kind === 'taken' || vinStatus?.kind === 'taken'}
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

// Brand / Inventory type / Tag are all lookup tables, editable in full at
// Settings → Lookups. What was missing was creating one mid-edit: to add a
// missing brand you had to navigate away and lose every unsaved change on the
// form. This renders the dropdown with an inline "+ New" that creates the entry
// and selects it in place.
type LookupFieldName = 'brand_id' | 'type_id' | 'tag_id';

function LookupSelect({
  name,
  label,
  noun,
  queryKey,
  list,
  create: createFn,
  newPlaceholder,
  register,
  setValue,
  error,
}: {
  name: LookupFieldName;
  label: string;
  /** Lowercase singular, used in the inline messages ("That brand already exists"). */
  noun: string;
  queryKey: string;
  list: () => Promise<Lookup[]>;
  create: (input: LookupInput) => Promise<Lookup>;
  newPlaceholder: string;
  register: UseFormRegister<InventoryFormValues>;
  setValue: UseFormSetValue<InventoryFormValues>;
  error?: ReactNode;
}) {
  const qc = useQueryClient();
  const options = useQuery({ queryKey: [queryKey], queryFn: list });
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const close = () => {
    setAdding(false);
    setNewName('');
    setAddError(null);
  };

  const select = (id: number) => setValue(name, id, { shouldDirty: true });

  const create = useMutation({
    mutationFn: (n: string) => createFn({ name: n }),
    onSuccess: (created) => {
      // flushSync so the new <option> is committed to the DOM *before* setValue
      // runs. setValue writes through to the select element, and assigning a
      // value that has no matching option is silently dropped by the DOM — the
      // entry would be created and internally selected while the dropdown still
      // showed "—". The invalidate then reconciles sort_order with the server.
      flushSync(() => {
        qc.setQueryData<Lookup[]>([queryKey], (prev) =>
          [...(prev ?? []), created].sort(
            (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
          ),
        );
      });
      select(created.id);
      qc.invalidateQueries({ queryKey: [queryKey] });
      close();
    },
    onError: (err) => {
      // 409 is the expected, actionable case: someone added that name already.
      setAddError(
        err instanceof ApiError && err.status === 409
          ? `That ${noun} already exists — pick it from the list.`
          : err instanceof Error
            ? err.message
            : `Could not add the ${noun}.`,
      );
    },
  });

  const submitNew = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    // Reusing an existing entry is almost certainly what was meant, and it keeps
    // the common case off the failing path entirely.
    const existing = options.data?.find((o) => o.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      select(existing.id);
      close();
      return;
    }
    create.mutate(trimmed);
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <label style={{ margin: 0 }}>{label}</label>
        {!adding ? (
          <button
            type="button"
            className="ghost"
            style={{ marginLeft: 'auto', padding: '0 6px', fontSize: '0.8rem' }}
            onClick={() => {
              setAdding(true);
              setAddError(null);
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
          >
            + New
          </button>
        ) : null}
      </div>

      <select {...register(name)}>
        <option value="">—</option>
        {options.data?.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>

      {adding ? (
        <div className="toolbar" style={{ marginTop: 6, gap: 6 }}>
          <input
            ref={inputRef}
            placeholder={newPlaceholder}
            maxLength={100}
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setAddError(null);
            }}
            // This input lives inside the listing <form>: a bare Enter would
            // submit the whole listing instead of adding the entry.
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitNew();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                close();
              }
            }}
          />
          <button
            type="button"
            className="primary"
            disabled={!newName.trim() || create.isPending}
            onClick={submitNew}
          >
            {create.isPending ? 'Adding…' : 'Add'}
          </button>
          <button type="button" className="ghost" onClick={close}>
            Cancel
          </button>
        </div>
      ) : null}

      {addError ? <div className="error-text">{addError}</div> : null}
      {error}
    </>
  );
}

// Schema field name → the label shown above that input, so the blocked-submit
// summary points at something the user can actually find on screen. Anything not
// listed here humanizes cleanly on its own (gross_capacity → "Gross capacity").
const FIELD_LABELS: Partial<Record<keyof InventoryFormValues, string>> = {
  inventory_id: 'Inventory ID',
  vin: 'VIN',
  brand_id: 'Brand',
  type_id: 'Inventory type',
  tag_id: 'Tag',
  gawr: 'GAWR',
  gross_capacity: 'GVWR',
  curb_weight: 'Dry weight',
  youtube_link: 'YouTube link',
  tires_wheels: 'Tires / wheels',
  biweekly_payment: 'Bi-weekly payment override',
  financing_disabled: 'Financing hidden',
};

function fieldLabel(name: keyof InventoryFormValues): string {
  const override = FIELD_LABELS[name];
  if (override) return override;
  const words = String(name).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// The fields whose submitted value differs from the record the form opened with.
//
// Why this exists: the edit form renders every column, so submitting the whole
// object PATCHed all ~45 fields on every save. One stale value anywhere in the
// record — an imported dimension longer than the API allowed, a name with a
// quote mark back when those were banned — then failed validation and took down
// the entire save, including the one field the user had actually touched. The
// only workaround was to edit the offending field too. Sending just the delta
// means untouched columns cannot block an unrelated edit.
//
// The baseline is round-tripped through FormSchema rather than compared against
// `initial` directly, so both sides have been through the same normalization
// (notably emptyToNull: a blank number input submits '' -> null, while the
// default is `undefined`; comparing raw would mark every empty price changed).
// If the stored record can't satisfy the form's own schema we can't trust the
// diff, so fall back to sending everything and let the API have the last word.
function changedFields(
  values: InventoryFormValues,
  initial?: Inventory,
): Partial<InventoryFormValues> {
  if (!initial) return values;
  const baseline = FormSchema.safeParse(toDefaults(initial));
  if (!baseline.success) return values;

  const changed: Partial<InventoryFormValues> = {};
  for (const key of Object.keys(values) as (keyof InventoryFormValues)[]) {
    if (values[key] !== baseline.data[key]) {
      changed[key] = values[key] as never;
    }
  }
  return changed;
}

function toDefaults(initial?: Inventory): Partial<InventoryFormValues> {
  if (!initial) {
    return {
      status: 'draft',
      condition: 'Used',
      year: new Date().getFullYear(),
    };
  }
  return {
    inventory_id: initial.inventory_id,
    status: initial.status,
    year: initial.year,
    regular_price: initial.regular_price ?? undefined,
    sale_price: initial.sale_price ?? undefined,
    biweekly_payment: initial.biweekly_payment ?? undefined,
    financing_disabled: initial.financing_disabled ?? false,
    vin: initial.vin ?? '',
    brand_id: initial.brand_id ?? null,
    type_id: initial.type_id ?? null,
    tag_id: initial.tag_id ?? null,
    vehicle_name: initial.vehicle_name,
    model_number: initial.model_number,
    mileage: initial.mileage ?? undefined,
    mileage_unit: initial.mileage_unit ?? null,
    location: initial.location ?? null,
    color: initial.color ?? '',
    condition: initial.condition,
    youtube_link: initial.youtube_link ?? '',
    description: initial.description,
    gawr: initial.gawr ?? undefined,
    gross_capacity: initial.gross_capacity ?? undefined,
    curb_weight: initial.curb_weight ?? undefined,
    payload_capacity: initial.payload_capacity ?? undefined,
    tie_downs: initial.tie_downs ?? '',
    lights: initial.lights ?? '',
    hitch: initial.hitch ?? '',
    jack: initial.jack ?? '',
    axles: initial.axles ?? '',
    suspensions: initial.suspensions ?? '',
    tires_wheels: initial.tires_wheels ?? '',
    rim_size: initial.rim_size ?? '',
    trailer_plug: initial.trailer_plug ?? '',
    ramp: initial.ramp ?? '',
    frame_construction: initial.frame_construction ?? '',
    deck_construction: initial.deck_construction ?? '',
    overall_length: initial.overall_length ?? '',
    overall_width: initial.overall_width ?? '',
    overall_height: initial.overall_height ?? '',
    interior_length: initial.interior_length ?? '',
    interior_width: initial.interior_width ?? '',
    interior_height: initial.interior_height ?? '',
    deck_length: initial.deck_length ?? '',
    deck_width: initial.deck_width ?? '',
    deck_height: initial.deck_height ?? '',
    enclosed_door_size: initial.enclosed_door_size ?? '',
  };
}
